import { getProviderIcon, type TersaModel, type TersaProvider } from '@/lib/providers';
import { cn } from '@/lib/utils';
import { useSubscription } from '@/providers/subscription';
import { useEffect, useMemo, useState } from 'react';
import { CheckIcon, ChevronsUpDownIcon, Crown, LockIcon, Star, Wrench, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/providers/i18n';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/kibo-ui/combobox';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';
import { getModelDisabled, isTagOnlyDenied, PLAN_DISPLAY, getModelAccessLabel, hasCustomAccessLabel } from '@/components/nodes/model-access';
import { useModelLabel } from '@/lib/models/use-model-label';
import { type DiscountSource, resolveDiscountMultiplier, resolveDiscountSource } from '@flowmuse/billing/discount-multiplier';

type ModelSelectorProps = {
  id?: string;
  options: Record<string, TersaModel>;
  value: string;
  width?: number | string | "auto";
  className?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
};

const ModelIcon = ({ data, chef, className }: { data: TersaModel; chef: TersaProvider; className?: string }) => {
  // 图像/视频模型注册表来自 @flowmuse/models（纯数据），chef 上没有 icon，按 id 兜底
  const Icon = data.icon ?? chef.icon ?? getProviderIcon(chef.id);
  if (!Icon) return null;
  return <Icon className={cn('size-5 shrink-0', className)} />;
};

// 第二行徽章基础样式（空心、圆角小标签）。各色仅在此之上追加配色。
const PILL_BASE = 'shrink-0 inline-flex items-center rounded-[4px] px-1.5 py-0.5 text-[9px] font-bold leading-none whitespace-nowrap';
const PILL_AMBER = `${PILL_BASE} bg-amber-500/15 text-amber-600 border border-amber-500/25`;
const PILL_EMERALD = `${PILL_BASE} bg-emerald-500/15 text-emerald-600 border border-emerald-500/25`;
const PILL_SKY = `${PILL_BASE} bg-sky-500/15 text-sky-500 border border-sky-500/25`;
const PILL_VIOLET = `${PILL_BASE} bg-violet-500/15 text-violet-500 border border-violet-500/25`;
// 第一行状态徽章（维护 / 关闭），带图标、固定行高。
const PILL_MUTED = 'shrink-0 inline-flex h-5 items-center gap-1 rounded-[4px] px-1.5 text-[9px] font-bold leading-none bg-muted text-muted-foreground border border-border whitespace-nowrap';

export const ModelSelector = ({ id, value, options, width = 250, className, onChange, disabled }: ModelSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);
  const isPreview = usePreviewCanvas();
  const effectiveDisabled = Boolean(disabled || isPreview);
  const activeModel = options[value];
  const { t, i18n } = useI18n();
  const { getLabel: getModelLabel, getNote: getModelNote, getCategory: getModelCategory, getBadges: getModelBadges } = useModelLabel();
  const { isSubscribed, modelAccessMap, discountMap, discountSourceMap, passDiscountMap } = useSubscription();

  // 折扣来源 → 展示名（全体=折扣 / 个人=专属折扣 / 套餐=套餐折扣 / 团队=团队折扣）。
  // 仅随语言变化重算，避免逐项渲染时重复 t() 查表。
  const discountSourceLabels = useMemo<Record<DiscountSource, string>>(() => ({
    global: t('Discount'),
    user: t('Exclusive discount'),
    plan: t('Plan discount'),
    org: t('Team discount'),
  }), [t]);

  useEffect(() => {
    try {
      const fav = localStorage.getItem('flowmuse_favorite_models');
      if (fav) setFavorites(JSON.parse(fav));
    } catch {}
  }, [open]);

  useEffect(() => { if (value && !options[value]) { const first = Object.keys(options)[0]; if (first) onChange?.(first); } }, [value, options, onChange]);

  const toggleFavorite = (e: React.MouseEvent, modelId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorites(prev => {
      const next = prev.includes(modelId) ? prev.filter(id => id !== modelId) : [...prev, modelId];
      try { localStorage.setItem('flowmuse_favorite_models', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Precompute grouping + per-item derived data once per relevant change so
  // toolbar re-renders don't re-run sorting / string allocation for every model.
  const { grouped, flatData } = useMemo(() => {
    const makeItem = (key: string, model: TersaModel, order: number) => {
      const label = getModelLabel(model);
      const note = getModelNote(model) ?? (model as any)?.note ?? '';
      const category = getModelCategory(model) ?? '';
      return {
        key,
        model,
        order,
        label,
        note,
        badges: getModelBadges(model),
        keywords: [
          label,
          (model as any)?.id ?? '',
          model.chef.name,
          note,
          category,
          (model as any)?.category ?? '',
        ].filter((s): s is string => typeof s === 'string' && s.length > 0),
      };
    };
    type Item = ReturnType<typeof makeItem>;

    const map = Object.entries(options).reduce((acc, [k, m]) => {
      const c = getModelCategory(m) ?? m.chef.name;
      const o = Number((m as any)?.order ?? 999);
      (acc[c] ??= { items: [] }).items.push(makeItem(k, m, o));
      return acc;
    }, {} as Record<string, { items: Item[] }>);

    Object.values(map).forEach(g => g.items.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label)));
    const baseGrouped = Object.entries(map)
      .map(([name, g]) => ({ name, items: g.items, minOrder: Math.min(...g.items.map(i => i.order)) }))
      .sort((a, b) => a.minOrder - b.minOrder || a.name.localeCompare(b.name));

    const favoriteItems = Object.entries(options)
      .filter(([k]) => favorites.includes(k))
      .map(([k, m]) => makeItem(k, m, -1));

    const grouped = favoriteItems.length > 0
      ? [{ name: t('Favorites'), items: favoriteItems, minOrder: -Infinity }, ...baseGrouped]
      : baseGrouped;

    const flatData = grouped.flatMap(g => g.items.map(it => ({ label: it.label, value: it.key })));
    return { grouped, flatData };
  }, [options, favorites, getModelLabel, getModelNote, getModelCategory, getModelBadges, t]);

  return (
    <Combobox
      open={open}
      onOpenChange={(next) => { if (effectiveDisabled) return; setOpen(next); }}
      data={flatData}
      type="model"
      value={value}
      onValueChange={(val) => { onChange?.(val); setOpen(false); }}
    >
      <ComboboxTrigger
        id={id}
        variant="ghost"
        className={cn('h-9 rounded-xl pl-1.5 pr-2.5 font-normal hover:bg-sidebar-accent focus-visible:ring-0 focus-visible:ring-offset-0 gap-2 border-none', className)}
        style={width === 'auto' ? {} : { width }}
        disabled={effectiveDisabled}
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <div className="flex items-center gap-2 overflow-hidden">
            {activeModel ? (
              <>
                <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-muted-foreground/80">
                  <ModelIcon data={activeModel} chef={activeModel.chef} className="size-4" />
                </span>
                <span className="truncate text-sm font-medium">{getModelLabel(activeModel)}</span>
              </>
            ) : (
              <span className="truncate text-sm text-muted-foreground">{t('Select model')}</span>
            )}
          </div>
          <ChevronsUpDownIcon size={14} className="shrink-0 text-muted-foreground opacity-50" />
        </div>
      </ComboboxTrigger>
      <ComboboxContent
        className="p-0"
        hideTitle
        popoverOptions={{ alignOffset: -8, className: 'fm-toolbar-surface w-[440px] max-w-[90vw] rounded-2xl border border-sidebar-border bg-sidebar/95 backdrop-blur-xl p-1.5 shadow-none' }}
      >
        <ComboboxInput className="h-9" />
        <ComboboxList className="custom-scrollbar max-h-[24rem]">
          <ComboboxEmpty />
          {grouped.map(({ name, items }) => (
            <ComboboxGroup key={name} heading={name} className="p-1">
              {items.map(({ key, model, label, note, badges, keywords }) => {
                const isSel = value === key;
                // 当前模式下不兼容的模型（图生图 / 视频转视频）：可见但提醒，不阻止选择。
                const modeIncompatible = (model as any)._modeIncompatible as string | undefined;
                const isDis = getModelDisabled(model, isSubscribed);
                const isAccessDenied = Boolean((model as any)._accessDenied);
                const isTagOnly = isTagOnlyDenied(model);
                const isFav = favorites.includes(key);
                const accessRule = modelAccessMap?.[key];
                // 管理端「关闭 / 维护」都会把 enabled 置为 false。这类是后台主动下线，
                // 不是订阅升级路径，点击不应跳转订阅页。
                const ruleDisabled = Boolean(accessRule) && accessRule!.enabled === false;
                const isMaintenance = ruleDisabled && Array.isArray(accessRule!.requiredTags) && accessRule!.requiredTags!.includes('maintenance');
                const isClosed = ruleDisabled && !isMaintenance;
                // Required plan only applies to models that are NOT admin-disabled
                // (close/maintenance). Prefer the live access rule, fall back to the
                // `_requiredPlan` meta attached by the subscription provider.
                const requiredPlanRaw = ruleDisabled
                  ? undefined
                  : (accessRule?.requiredPlan && accessRule.requiredPlan !== '*'
                      ? accessRule.requiredPlan
                      : ((model as any)._requiredPlan && (model as any)._requiredPlan !== '*'
                          ? (model as any)._requiredPlan as string
                          : undefined));
                const requiresPlan = Boolean(requiredPlanRaw);
                const isHighPrice = model.priceIndicator === 'highest' || model.priceIndicator === 'high';
                const isMemberModel = !isTagOnly && !ruleDisabled && (requiresPlan || isHighPrice);
                // 需订阅解锁（无权限灰显或高价位未订阅）。标签放到第二行最前面展示。
                // 排除 tag-only / 维护 / 关闭：这些已在第一行用各自徽标表达，解锁路径也并非订阅。
                const showUnlock = !isTagOnly && !isMaintenance && !isClosed
                  && (isAccessDenied || (isDis && isHighPrice));
                const planLabel = requiredPlanRaw ? `${PLAN_DISPLAY[requiredPlanRaw] || requiredPlanRaw}+` : '';
                const tagLabel = isTagOnly ? getModelAccessLabel(model, t, i18n?.language) : '';
                // 自定义准入文案（如「即将上线」）原样展示，不套「需要 xx 权限」模板。
                const tagLabelIsCustom = isTagOnly && hasCustomAccessLabel(model, i18n?.language);
                // 当前用户对该模型的有效折扣（已合并 全局 / 套餐 / 用户 / 团队）。
                const discountPct = Math.round((1 - resolveDiscountMultiplier(discountMap, key)) * 100);
                const showDiscount = discountPct > 0 && !isMaintenance && !isClosed;
                const discountLabel = showDiscount
                  ? discountSourceLabels[resolveDiscountSource(discountSourceMap, key) ?? 'global']
                  : '';
                // 模型卡（次卡）折扣：与积分折扣独立，只作用于次卡消耗数量。
                const passDiscountPct = Math.round((1 - resolveDiscountMultiplier(passDiscountMap, key)) * 100);
                const showPassDiscount = passDiscountPct > 0 && !isMaintenance && !isClosed;
                return (
                  <ComboboxItem
                    key={`${name}::${key}`}
                    value={`${name}::${key}`}
                    keywords={keywords}
                    onSelect={() => {
                      if (isMaintenance) { setOpen(false); toast.info(t('This model is under maintenance, please try again later.')); return; }
                      if (isClosed) { setOpen(false); toast.info(t('This model is currently unavailable.')); return; }
                      if (isTagOnly) { setOpen(false); toast.info(t('This model is not yet available to you; requires the appropriate permission.')); return; }
                      // maintenance/closed/tagOnly 已在上面拦截，此处 showUnlock 等价于 isAccessDenied || (isDis && 高价位)。
                      if (showUnlock) { setOpen(false); window.dispatchEvent(new Event('open_plans_pricing')); return; }
                      // 不兼容当前模式但仍允许选择：仅提醒，不阻止。
                      if (modeIncompatible === 'i2i') { toast.info(t('This model does not support image-to-image; switching to text-to-image.')); }
                      else if (modeIncompatible === 'v2v') { toast.info(t('This model does not support video-to-video; the incoming video will be ignored.')); }
                      onChange?.(key); setOpen(false);
                    }}
                    className={cn(
                      'group rounded-lg py-2 my-1 min-h-[3.5rem] focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer gap-2.5 items-center',
                      (isDis || isMaintenance) && 'opacity-70',
                      isSel && 'bg-sidebar-accent',
                    )}
                  >
                    <span className="relative shrink-0 inline-flex">
                      <ModelIcon data={model} chef={model.chef} className={cn('size-[30px]', (isDis || isTagOnly) && 'opacity-50 grayscale')} />
                      {(model as any).isNew && (
                        <span className="absolute -top-1.5 -right-2 rounded-[3px] bg-primary px-0.5 text-[7px] font-bold leading-[1.4] text-primary-foreground">NEW</span>
                      )}
                    </span>
                    <div className="relative flex-1 min-w-0 flex flex-col justify-center">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{label}</span>
                        {(showUnlock || isTagOnly) && (
                          <LockIcon size={12} className="shrink-0 text-amber-500" aria-label={isTagOnly ? t('No access') : t('Subscribe to unlock')} />
                        )}
                        {isMaintenance ? (
                          <span className={PILL_MUTED}>
                            <Wrench size={10} className="shrink-0" />
                            {t('Under maintenance')}
                          </span>
                        ) : isClosed ? (
                          <span className={PILL_MUTED}>
                            <Ban size={10} className="shrink-0" />
                            {t('Unavailable')}
                          </span>
                        ) : (!showUnlock && isMemberModel) ? (
                          <Crown size={12} className="shrink-0 fill-amber-400 text-amber-500" aria-label={t('Member exclusive')} />
                        ) : null}
                      </div>
                      {(showUnlock || isTagOnly || showDiscount || showPassDiscount || modeIncompatible || note || badges.length > 0) && (
                        <div className="mt-0.5 flex min-h-[1.125rem] flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
                          {modeIncompatible === 'i2i' && (
                            <span className={PILL_AMBER}>{t('No image-to-image')}</span>
                          )}
                          {modeIncompatible === 'v2v' && (
                            <span className={PILL_AMBER}>{t('No video-to-video')}</span>
                          )}
                          {showUnlock && (
                            <span className={PILL_AMBER}>
                              {planLabel ? t('{{plan}} subscription to unlock', { plan: planLabel }) : t('Subscribe to unlock')}
                            </span>
                          )}
                          {isTagOnly && (
                            <span className={PILL_AMBER}>
                              {tagLabel
                                ? (tagLabelIsCustom ? tagLabel : t('Requires {{permission}}', { permission: tagLabel }))
                                : t('No access')}
                            </span>
                          )}
                          {showDiscount && (
                            <span className={PILL_EMERALD} aria-label={t('{{pct}}% off', { pct: discountPct })}>
                              {discountLabel} -{discountPct}%
                            </span>
                          )}
                          {showPassDiscount && (
                            <span className={PILL_VIOLET} aria-label={t('{{pct}}% off', { pct: passDiscountPct })}>
                              {t('Model pass discount')} -{passDiscountPct}%
                            </span>
                          )}
                          {badges.map((badge) => (
                            <span key={badge} className={PILL_SKY}>{badge}</span>
                          ))}
                          {note && (
                            <span className={cn(
                              'min-w-0 truncate text-[11px] leading-snug text-muted-foreground',
                              isSel ? 'visible' : 'invisible group-data-[selected=true]:visible',
                            )}>{note}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => toggleFavorite(e, key)}
                      aria-label={isFav ? t('Remove from favorites') : t('Add to favorites')}
                      className={cn(
                        'p-1 rounded-md transition-opacity shrink-0',
                        isFav ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100',
                      )}
                    >
                      <Star size={13} className={cn('transition-colors', isFav ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/50 hover:text-muted-foreground')} />
                    </button>
                    <CheckIcon className={cn('size-4 text-primary shrink-0', isSel ? 'opacity-100' : 'opacity-0')} />
                  </ComboboxItem>
                );
              })}
            </ComboboxGroup>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
